import {
  useMutation,
  useQuery,
  useQueryClient,
  UseQueryResult,
} from '@tanstack/react-query';
import { useCallback } from 'react';

import noop from 'lodash/noop';

import { useSnackBar } from '@sbv-clients/ui-shared';
import { getApiErrorMessage, UnwrapArray } from '@sbv-clients/util-misc';

import { ScheduleCacheKeys } from './util/cache-keys';
import {
  Api,
  CommentTinyResponse,
  HourMultiDeleteRequestV2,
  HourTinyResponse,
  HourV2ApiApiV2HourOtherHoursConfigurationEmployeeIdGetRequest,
  HourV2ApiApiV2HourOtherHoursGetRequest,
  HourV2ApiApiV2HourPayPeriodSummaryGetRequest,
  HoursDetailedBulkAddRequestV2,
  OtherHourUpdateRequestV2,
  OtherHoursPayTypeConfigurationResponseV2,
  useApi,
} from '../index';
import {
  HourCacheKeys,
  TimeCardCacheKeys,
  AccrualsKeys,
  EmployeeCacheKeys,
} from './util/cache-keys';

const useUpdateCache = () => {
  const queryClient = useQueryClient();
  return useCallback(async () => {
    queryClient.invalidateQueries([HourCacheKeys.PBJ_HOURS]).catch(noop);
    queryClient.invalidateQueries([HourCacheKeys.DAILY_SUMMARY]).catch(noop);
    queryClient
      .invalidateQueries([HourCacheKeys.PAY_PERIOD_SUMMARY])
      .catch(noop);
    queryClient
      .invalidateQueries([TimeCardCacheKeys.PAY_PERIOD_APPROVAL])
      .catch(noop);
    queryClient.invalidateQueries([AccrualsKeys.ACCRUALS_BALANCE]).catch(noop);
    queryClient.invalidateQueries([HourCacheKeys.OTHER_HOURS]).catch(noop);
    queryClient
      .invalidateQueries([EmployeeCacheKeys.EmployeeHeader])
      .catch(noop);
    queryClient
      .invalidateQueries([ScheduleCacheKeys.EMPLOYEE_PLACEHOLDERS])
      .catch(noop);
    queryClient
      .invalidateQueries([EmployeeCacheKeys.EMPLOYEE_UNDO_STACK])
      .catch(noop);
    await queryClient
      .invalidateQueries([TimeCardCacheKeys.EMPLOYEE_TIME_CARD])
      .catch(noop);
  }, [queryClient]);
};

export const useHourAuditsGet = (employeeId: number, recordId: number) => {
  const api = useApi();
  return useQuery({
    queryKey: [HourCacheKeys.HOUR_AUDITS, { employeeId, recordId }],
    queryFn: () =>
      api.hourV2Api.apiV2HourAuditsGet({
        employeeId,
        recordId,
      }),
    retry: false,
    refetchOnWindowFocus: false,
  });
};

export type HourAddResponse = HourTinyResponse;

export const useDailySummaryGet = (employeeId: number, payDate: string) => {
  const api = useApi();
  return useQuery({
    queryKey: [HourCacheKeys.DAILY_SUMMARY, { employeeId, payDate }],
    queryFn: () =>
      api.hourV2Api
        .apiV2HourDailySummaryGet({
          employeeId,
          payDate,
        })
        .then((response) => response?.data || []),
    retry: false,
    refetchOnWindowFocus: false,
    enabled: Boolean(employeeId) && Boolean(payDate),
  });
};

export type HoursSummaryRow = NonNullable<
  UnwrapArray<ReturnType<typeof useDailySummaryGet>['data']>
>;

export const usePayPeriodSummaryGet = (
  body: HourV2ApiApiV2HourPayPeriodSummaryGetRequest
) => {
  const api = useApi();
  return useQuery({
    queryKey: [HourCacheKeys.PAY_PERIOD_SUMMARY, body],
    queryFn: () =>
      api.hourV2Api
        .apiV2HourPayPeriodSummaryGet(body)
        .then((response) => response?.data ?? []),
    retry: false,
    refetchOnWindowFocus: false,
  });
};

export const useAddDetailedHours = () => {
  const api = useApi();
  const updateCache = useUpdateCache();
  return useMutation(
    (body: HoursDetailedBulkAddRequestV2) =>
      api.hourV2Api.apiV2HourDetailsBulkPost({
        hoursDetailedBulkAddRequestV2: body,
      }),
    {
      onSuccess: async () => {
        await updateCache();
      },
    }
  );
};

const updateOtherHour =
  (api: Api) => (otherHourUpdateRequestV2?: OtherHourUpdateRequestV2) => {
    return api.hourV2Api.apiV2HourOtherHourPut({ otherHourUpdateRequestV2 });
  };

export const useUpdateOtherHour = () => {
  const { notifyError } = useSnackBar();

  const api = useApi();
  const updateCache = useUpdateCache();
  return useMutation(updateOtherHour(api), {
    onSuccess: async () => {
      await updateCache();
    },
  });
};

export type OtherHourUpdateRequest = OtherHourUpdateRequestV2;

type DeleteHoursRequest = {
  pkEmployeeId: number;
  pkHoursIDs: number[];
};
const deleteOtherHours =
  (api: Api) =>
  ({ pkEmployeeId, pkHoursIDs }: DeleteHoursRequest) => {
    const hourMultiDeleteRequestV2: HourMultiDeleteRequestV2 = {
      employeeId: pkEmployeeId,
      hourIds: pkHoursIDs,
    };
    return api.hourV2Api.apiV2HourMultiDelete({ hourMultiDeleteRequestV2 });
  };

export const useDeleteOtherHours = () => {
  const api = useApi();
  const updateCache = useUpdateCache();
  return useMutation(deleteOtherHours(api), {
    onSuccess: async () => {
      await updateCache();
    },
    onError: async (error) => {
      await updateCache();
    },
  });
};

export const useOtherHoursGet = (
  body: HourV2ApiApiV2HourOtherHoursGetRequest
) => {
  const api = useApi();
  return useQuery({
    queryKey: [HourCacheKeys.OTHER_HOURS, body],
    queryFn: () =>
      api.hourV2Api
        .apiV2HourOtherHoursGet(body)
        .then((response) => response.data ?? []),
    retry: false,
    refetchOnWindowFocus: false,
    enabled: Boolean(body.startDate) && Boolean(body.endDate),
  });
};

type OtherHoursResponse = ReturnType<typeof useOtherHoursGet>['data'];
export type OtherHoursResponseItem = NonNullable<
  UnwrapArray<OtherHoursResponse>
>;
export type CommentResponse = CommentTinyResponse;

export const useOtherHoursConfiguration = (
  body: HourV2ApiApiV2HourOtherHoursConfigurationEmployeeIdGetRequest
) => {
  const api = useApi();
  return useQuery({
    queryKey: [HourCacheKeys.OTHER_HOURS_FOR_CONFIGURATION, body],
    queryFn: () =>
      api.hourV2Api
        .apiV2HourOtherHoursConfigurationEmployeeIdGet(body)
        .then((response) => response.data),
    retry: false,
    refetchOnWindowFocus: false,
    enabled: !!body.employeeId,
  });
};

export const useOtherHoursPayTypeConfigurationGet = (
  employeeId: number,
  payTypeId: number,
  payDate: string
): UseQueryResult<OtherHoursPayTypeConfigurationResponseV2> => {
  const api = useApi();
  const isEnabled = payTypeId !== null;
  return useQuery({
    queryKey: [
      HourCacheKeys.OTHER_HOURS_FOR_PAY_DATE_CONFIGURATION,
      { employeeId, payTypeId, payDate },
    ],
    queryFn: () =>
      api.hourV2Api
        .apiV2HourOtherHoursPayTypeConfigurationGet({
          employeeId,
          payTypeId,
          payDate,
        })
        .then((response) => response.data),
    retry: false,
    refetchOnWindowFocus: false,
    enabled: isEnabled,
  });
};
export const usePBJHoursGet = (
  employeeId: number,
  startDate: string,
  endDate: string
) => {
  const api = useApi();
  return useQuery({
    queryKey: [
      HourCacheKeys.PBJ_HOURS,
      {
        employeeId,
        startDate,
        endDate,
      },
    ],
    queryFn: () =>
      api.pbjApi
        .pbjPBJHoursGet({
          employeeId,
          startDate,
          endDate,
        })
        .then((response) => response.data ?? []),
    retry: false,
    refetchOnWindowFocus: false,
    enabled: employeeId > 0 && Boolean(startDate) && Boolean(endDate),
  });
};
